import React from 'react';
import { View, StyleSheet, TouchableOpacity, StatusBar } from 'react-native';
import { Text } from '@/components/common/text';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import {
  useThemeColors,
  useThemedStyles,
  type ThemeContextValue,
} from '@/hooks/shared/useThemeColors';
import { PaymentIcons } from './PaymentIcons';

interface MobileScreenHeaderProps {
  title: string;
  onBack?: () => void;
  showBackButton?: boolean;
}

/**
 * Shared mobile screen header component
 * Provides consistent header layout across payment screens
 */
export const MobileScreenHeader: React.FC<MobileScreenHeaderProps> = ({ title, onBack, showBackButton = true }) => {
  const navigation = useNavigation();
  const { mode, semantic } = useThemeColors();
  const styles = useThemedStyles(makeStyles);

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigation.goBack();
    }
  };

  return (
    <>
      <StatusBar
        barStyle={mode === 'dark' ? 'light-content' : 'dark-content'}
        backgroundColor={semantic.background.primary}
      />
      <SafeAreaView edges={['top']} style={styles.headerSafeArea}>
        <View style={styles.header}>
          {showBackButton ? (
            <TouchableOpacity onPress={handleBack} style={styles.backButton}>
              <PaymentIcons.ChevronLeft />
            </TouchableOpacity>
          ) : (
            <View style={styles.headerLeft} />
          )}
          <Text style={styles.headerTitle}>{title}</Text>
          <View style={styles.headerRight} />
        </View>
      </SafeAreaView>
    </>
  );
};

const makeStyles = ({ semantic }: ThemeContextValue) =>
  StyleSheet.create({
    headerSafeArea: {
      backgroundColor: semantic.background.primary,
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 16,
      paddingVertical: 12,
    },
    backButton: {
      padding: 4,
    },
    headerLeft: {
      width: 32,
    },
    headerTitle: {
      fontSize: 17,
      fontWeight: '600',
      color: semantic.text.primary,
    },
    headerRight: {
      width: 32,
    },
  });
